import { AddConditionDialog } from '@/components/molecules/AddConditionDialog';
import { RecordListScroll } from '@liza/ui/components/RecordListScroll';
import { useInfiniteRecordList } from '@liza/ui/hooks/useInfiniteRecordList';
import { useTRPC, useTRPCClient } from '@/utils/trpc';
import type { PatientAdminApiRouterOutputs } from '@liza/patient-admin-api';
import { ConditionsList } from '@liza/ui/components/records/ConditionsList';
import {
  type RecordsListFilters,
  defaultFilters,
  RecordsListControls,
} from '@liza/ui/components/records/RecordsListControls';
import { useRecordPromotionStatus } from '@liza/ui/hooks/useRecordPromotionStatus';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDebouncedCallback } from 'use-debounce';
import type { DetailPanelView } from '../types';

type Patient = PatientAdminApiRouterOutputs['patient']['get']['patient'];

type Props = {
  onViewChange: (view: DetailPanelView | null) => void;
  patient: Patient;
  selectedId?: string;
};

export const Conditions = ({ onViewChange, patient, selectedId }: Props): React.ReactElement => {
  const { t } = useTranslation('records');
  const trpc = useTRPC();
  const trpcClient = useTRPCClient();
  const queryClient = useQueryClient();

  const [filters, setFilters] = useState<RecordsListFilters>(defaultFilters);
  const handleFiltersChange = useDebouncedCallback(setFilters, 300);
  const [isAddOpen, setIsAddOpen] = useState(false);

  const listOptions = {
    ownership: filters.ownership,
    searchParams: { 'code:text': filters.searchQuery },
    status: filters.status,
  };

  const {
    items: conditions,
    query,
    bottomSentinelRef,
  } = useInfiniteRecordList({
    queryKey: trpc.patient.condition.list.queryKey({ medplumPatientId: patient.medplumId, options: listOptions }),
    queryFn: async ({ offset, count }) => {
      const res = await trpcClient.patient.condition.list.query({
        medplumPatientId: patient.medplumId,
        options: listOptions,
        offset,
        count,
      });
      return { items: res.conditions, hasMore: res.hasMore };
    },
  });

  const { mutateAsync: createCondition } = useMutation(
    trpc.patient.condition.create.mutationOptions({
      onSuccess: () => {
        void queryClient.invalidateQueries({
          queryKey: trpc.patient.condition.list.queryKey({ medplumPatientId: patient.medplumId }),
        });
      },
    }),
  );

  const { promote, getPromotionStatus } = useRecordPromotionStatus();

  const handlePromotionClick = useCallback(
    (id: string): void => {
      const source = conditions.find((c) => c.id === id);
      if (!source) return;

      void promote(id, async () =>
        createCondition({
          resource: {
            ...source,
            id: undefined,
            subject: { reference: `Patient/${patient.medplumId}` },
            recordedDate: new Date().toISOString(),
          },
        }),
      );
    },
    [createCondition, conditions, patient, promote],
  );

  return (
    <div className="flex flex-col gap-2 p-2">
      <RecordsListControls
        title={t('conditions')}
        searchPlaceholder={t('searchConditions')}
        onFiltersChange={handleFiltersChange}
        onAdd={() => setIsAddOpen(true)}
        addLabel={t('addCondition')}
      />
      <AddConditionDialog medplumPatientId={patient.medplumId} isOpen={isAddOpen} onOpenChange={setIsAddOpen} />
      <RecordListScroll
        isLoading={query.isLoading}
        isError={query.isError}
        error={query.error}
        isFetchingNextPage={query.isFetchingNextPage}
        bottomSentinelRef={bottomSentinelRef}
      >
        <ConditionsList
          conditions={conditions}
          onSelectCondition={(id) =>
            onViewChange({ type: 'condition-detail', id, backView: { type: 'conditions-list' } })
          }
          selectedConditionId={selectedId}
          onPromotionClick={filters.ownership === 'external' ? handlePromotionClick : undefined}
          getPromotionStatus={getPromotionStatus}
        />
      </RecordListScroll>
    </div>
  );
};
